import { bannerOptionsUpdate } from '../../../operations/admin/bannerOptionsUpdate';
import { Box, Checkbox, Field, Flex, Group, Heading, Input, Text, VStack } from '@chakra-ui/react';
import { enrollmentAvailableUpdate } from '../../../operations/admin/enrollmentAvailableUpdate';
import { InfoTip } from '../../../components/ui/toogle-tip';
import { toaster } from '../../../components/ui/toaster';
import { Tooltip } from '../../../components/ui/tooltip';
import { useDatabase } from '../../../hooks/useDatabase';
import { useEffect, useState } from 'react';

export type BannerOptions = {
  visible: boolean;
  text: string;
};

type EnrollmentFormProps = {
  value?: boolean;
  bannerOptions?: BannerOptions;
};

export const EnrollmentForm = ({ value, bannerOptions }: EnrollmentFormProps) => {
  const database = useDatabase();
  const [checked, setChecked] = useState(Boolean(value));
  const [draftText, setDraftText] = useState(bannerOptions?.text ?? '');
  const [bannerVisible, setBannerVisible] = useState(Boolean(bannerOptions?.visible));

  useEffect(() => {
    setChecked(Boolean(value));
    setDraftText(bannerOptions?.text ?? '');
    setBannerVisible(Boolean(bannerOptions?.visible));
  }, [value, bannerOptions]);

  const isTextEmpty = draftText.trim().length === 0;

  const handleCheckedChange = async (nextChecked: boolean) => {
    setChecked(nextChecked);

    try {
      await enrollmentAvailableUpdate(database, nextChecked);
      toaster.create({
        title: 'Zapisano',
        type: 'success',
        duration: 2500,
      });
    } catch (error) {
      console.error('Failed to update enrollment availability', error);
      toaster.create({
        title: 'Nie udało się zapisać zmian',
        type: 'error',
        duration: 3000,
      });
    }
  };

  const saveBannerOptions = async (nextVisible: boolean, nextText: string) => {
    const normalizedText = nextText.trim();
    const nextValue = {
      visible: normalizedText.length > 0 && nextVisible,
      text: normalizedText,
    };

    setBannerVisible(nextValue.visible);
    await bannerOptionsUpdate(database, nextValue);
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextText = event.target.value;
    setDraftText(nextText);

    if (!nextText.trim()) {
      setBannerVisible(false);
    }
  };

  const handleTextSave = async () => {
    const normalizedText = draftText.trim();
    await saveBannerOptions(!isTextEmpty && bannerVisible, normalizedText);
  };

  const bannerCheckbox = (
    <Tooltip
      content="Aby pokazać baner, najpierw wpisz tekst banera."
      disabled={!isTextEmpty}
      positioning={{ placement: 'top' }}
    >
      <Box display="inline-flex">
        <Checkbox.Root
          checked={bannerVisible}
          disabled={isTextEmpty}
          onCheckedChange={async (event) => {
            if (isTextEmpty) return;

            const nextVisible = event.checked === true;
            setBannerVisible(nextVisible);
            await saveBannerOptions(nextVisible, draftText);
          }}
        >
          <Checkbox.HiddenInput />
          <Checkbox.Control>
            <Checkbox.Indicator />
          </Checkbox.Control>
          <Checkbox.Label>Widoczny</Checkbox.Label>
        </Checkbox.Root>
      </Box>
    </Tooltip>
  );

  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={{ base: 4, md: 5 }} bg="white">
      <VStack align="stretch" gap={4}>
        <Flex align="center" gap={2}>
          <Heading as="span" size="sm" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
            Zapisy i baner
          </Heading>
        </Flex>

        <Field.Root>
          <Checkbox.Root
            checked={checked}
            onCheckedChange={(event) => handleCheckedChange(event.checked === true)}
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control>
              <Checkbox.Indicator />
            </Checkbox.Control>
            <Checkbox.Label>
              <Group gap={1}  >
                <Text>Zapisy dostępne</Text>
                <InfoTip
                  content="Włączenie tej opcji pokazuje przyciski i formularz zapisów na stronie głównej i w nawigacji."
                  positioning={{ placement: 'right' }}
                  contentProps={{
                    bg: 'gray.50',
                    color: 'gray.800',
                    borderWidth: '1px',
                    borderColor: 'gray.100',
                    borderRadius: 'md',
                    fontSize: 'xs',
                    boxShadow: 'sm',
                  }}
                />
              </Group>
            </Checkbox.Label>
          </Checkbox.Root>
        </Field.Root>

        <Flex align="center" gap={6} w="100%" flexWrap="nowrap">
          {bannerCheckbox}

          <Field.Root flex={1} minW={0}>
            <Field.Label srOnly>Tekst banera</Field.Label>
            <Input
              value={draftText}
              onChange={handleTextChange}
              onBlur={handleTextSave}
              placeholder="Zapisy na rok szkolny 2026/2027 już otwarte!"
            />
          </Field.Root>
        </Flex>
      </VStack>
    </Box>
  );
};

export default EnrollmentForm;
